import { Alert, Input, Select } from 'antd'
import React from 'react'
import styled from 'styled-components'
import { FlightSetType, RooftopFlightDataData } from 'types'

const ConfigContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem;
`

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ theme }) => theme.forecast.text1};
  margin-bottom: 0.5rem;
`

const StyledSelect = styled(Select)`
  width: 100%;

  .ant-select-selector {
    background-color: ${({ theme }) => theme.forecast.background2} !important;
    border: 1px solid ${({ theme }) => theme.forecast.background4} !important;
    color: ${({ theme }) => theme.forecast.text1} !important;
  }

  .ant-select-arrow {
    color: ${({ theme }) => theme.forecast.text2};
  }
`

const StyledInput = styled(Input)`
  background-color: ${({ theme }) => theme.forecast.background2} !important;
  border: 1px solid ${({ theme }) => theme.forecast.background4} !important;
  color: ${({ theme }) => theme.forecast.text1} !important;

  &::placeholder {
    color: ${({ theme }) => theme.forecast.text3};
  }
`

const FLIGHT_SET_OPTIONS: Array<{ label: string; value: FlightSetType }> = [
  { label: 'Day RGB', value: 'day-rgb' },
  { label: 'Day IR', value: 'day-ir' },
  { label: 'Night IR', value: 'night-ir' },
]

const WEATHER_TYPE_OPTIONS = [
  { label: 'Clear', value: 'clear' },
  { label: 'Cloudy', value: 'cloudy' },
  { label: 'Overcast', value: 'overcast' },
  { label: 'Light Rain', value: 'light-rain' },
  { label: 'Rain', value: 'rain' },
  { label: 'Fog', value: 'fog' },
  { label: 'Windy', value: 'windy' },
  { label: 'Other', value: 'other' },
]

interface FlightSetConfigProps {
  flightSetType: FlightSetType | undefined
  weatherType: string
  notes: string
  existingEntry?: { data: RooftopFlightDataData } | undefined
  onFlightSetTypeChange: (type: FlightSetType) => void
  onWeatherTypeChange: (type: string) => void
  onNotesChange: (notes: string) => void
}

export const FlightSetConfig: React.FC<FlightSetConfigProps> = ({
  flightSetType,
  weatherType,
  notes,
  existingEntry,
  onFlightSetTypeChange,
  onWeatherTypeChange,
  onNotesChange,
}) => {
  return (
    <ConfigContainer>
      {existingEntry && flightSetType && (
        <Alert
          title='Existing Flight Data Found'
          description={`This component already has flight data for ${flightSetType
            .split('-')
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(
              ' '
            )}. You can update the weather and notes below, or upload additional photos in the next step.`}
          type='info'
          showIcon
          style={{ marginBottom: '1rem' }}
        />
      )}

      <div>
        <Label>Flight Set Type *</Label>
        <StyledSelect
          placeholder='Select flight set type...'
          value={flightSetType}
          onChange={(value) => onFlightSetTypeChange(value as FlightSetType)}
          options={FLIGHT_SET_OPTIONS}
        />
      </div>

      <div>
        <Label>Weather Type *</Label>
        <StyledSelect
          placeholder='Select weather type...'
          value={weatherType}
          onChange={(value) => onWeatherTypeChange(value as string)}
          options={WEATHER_TYPE_OPTIONS}
        />
      </div>

      <div>
        <Label>Notes (Optional)</Label>
        <StyledInput.TextArea
          placeholder='Add any additional notes about this flight...'
          value={notes}
          onChange={(e) => onNotesChange(e.target.value)}
          rows={3}
        />
      </div>
    </ConfigContainer>
  )
}
